import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Coins, Sparkles, Sliders } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const CONTROLS = [
    { key: "projects_per_year",     label: "Projects per year",             min: 1,   max: 24,   step: 1 },
    { key: "hours_per_project",     label: "Avg. hours per project",        min: 4000,max: 60000,step: 1000 },
    { key: "reuse_pct",             label: "Portfolio reuse %",             min: 0,   max: 90,   step: 1 },
    { key: "blended_rate_usd",      label: "Blended engineering rate ($/hr)",min: 80, max: 250,  step: 5 },
    { key: "bid_prep_days_before",  label: "Bid prep days · before",        min: 5,   max: 60,   step: 1 },
    { key: "bid_prep_days_after",   label: "Bid prep days · after",         min: 1,   max: 30,   step: 1 },
    { key: "procurement_days_before",label:"Procurement cycle days · before",min:20,  max: 200,  step: 2 },
    { key: "procurement_days_after", label:"Procurement cycle days · after", min: 10, max: 160,  step: 2 },
    { key: "rework_reduction_pct",  label: "Rework reduction %",            min: 0,   max: 95,   step: 1 },
];

const DEFAULTS = {
    projects_per_year: 8, hours_per_project: 24000, reuse_pct: 47,
    blended_rate_usd: 150, bid_prep_days_before: 24, bid_prep_days_after: 11,
    procurement_days_before: 112, procurement_days_after: 68, rework_reduction_pct: 67,
};

export default function ValueCalculator() {
    const [inputs, setInputs] = useState(DEFAULTS);
    const [outputs, setOutputs] = useState(null);
    const [components, setComponents] = useState([]);

    useEffect(() => {
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/edi/value-calculator", inputs);
                setOutputs(data.outputs);
                setComponents(data.components);
            } catch { toast.error("Value Calculator failed"); }
        }, 200);
        return () => clearTimeout(t);
    }, [inputs]);

    const set = (k, v) => setInputs((s) => ({ ...s, [k]: v }));

    return (
        <div className="space-y-8" data-testid={EDI.valueCalculatorRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Executive Value Calculator™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Continuous, live calculation of enterprise engineering value.
                        Every slider updates the executive impact — hours saved, cost avoided,
                        ROI and net annual benefit.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <Sliders className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">Live executive model</span>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                {/* Controls */}
                <div className="glass rounded-xl p-6 xl:col-span-1">
                    <div className="kicker text-slate-500 text-[10px] mb-4">Inputs</div>
                    <div className="space-y-4">
                        {CONTROLS.map(({ key, label, min, max, step }) => (
                            <div key={key}>
                                <div className="flex items-center justify-between">
                                    <div className="text-xs text-slate-300 font-body">{label}</div>
                                    <div className="text-[var(--sgsb-gold)] font-display font-bold text-sm tabular-nums">{inputs[key]}</div>
                                </div>
                                <input
                                    data-testid={EDI.valueCalculatorSlider(key)}
                                    type="range"
                                    min={min} max={max} step={step}
                                    value={inputs[key]}
                                    onChange={(e) => set(key, Number(e.target.value))}
                                    className="w-full accent-[color:var(--sgsb-gold)]"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Outputs */}
                <div className="xl:col-span-2 space-y-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {outputs && [
                            { l: "Hours Saved",       v: outputs.hours_saved.toLocaleString(), s: "per year" },
                            { l: "Engineer Years",    v: outputs.engineer_years,               s: "1,800 hrs / yr" },
                            { l: "Cost Saved",        v: outputs.cost_saved,                   s: "annualised" },
                            { l: "ROI",               v: `${outputs.roi_pct}%`,                s: `vs ${outputs.annual_platform_cost} run-rate` },
                            { l: "Net Annual Benefit",v: outputs.net_annual_benefit,           s: "post-platform-cost" },
                            { l: "Bid Prep Saving",   v: `${outputs.bid_prep_saving_pct}%`,    s: "cycle time" },
                            { l: "Procurement Saving",v: `${outputs.procurement_saving_pct}%`, s: "cycle time" },
                            { l: "Rework Reduction",  v: `${outputs.rework_reduction_pct}%`,   s: "engineering rework" },
                        ].map(({ l, v, s }) => (
                            <div key={l} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                                <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                                <Sparkles className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <div className="mt-2 kicker text-slate-400 text-[10px]">{l}</div>
                                <div className="mt-2 font-display font-black text-white text-xl tracking-tighter">{v}</div>
                                <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                            </div>
                        ))}
                    </div>

                    <div className="glass rounded-xl p-6">
                        <div className="flex items-center gap-2">
                            <Coins className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                            <h3 className="font-display font-black text-white tracking-tight">Value components ($M)</h3>
                        </div>
                        <div className="h-[280px] mt-4 -ml-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={components} layout="vertical" margin={{ left: 40 }}>
                                    <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" horizontal={false} />
                                    <XAxis type="number" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                    <YAxis dataKey="label" type="category" width={200} stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                    <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }} />
                                    <Bar dataKey="value_musd" name="Value ($M)" fill="#D4AF37" radius={[0, 4, 4, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
